import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowSquareOut,
  BookOpenText,
  CheckCircle,
  Download,
  Key,
  Sparkle,
  type Icon,
} from '@phosphor-icons/react';

import './index.less';

const HERO_META = [
  '开源剪映小助手',
  '扣子 / MCP 插件定制',
  '支持私有化部署',
  '工作流深度集成',
] as const;

const SECTION_SHORTCUTS = [
  { href: '#jianying', label: '剪映小助手' },
  { href: '#video-studio', label: '智能视频创作' },
  { href: '#scene-split', label: '镜头分割' },
  { href: '#media-ops', label: '内容运营' },
  { href: '#values', label: '核心价值' },
] as const;

interface HeroEntry {
  key: string;
  tag: string;
  title: string;
  desc: string;
  cta: string;
  icon: Icon;
  accent: 'download' | 'api' | 'docs';
  to?: string;
  href?: string;
}

const HERO_ENTRIES: HeroEntry[] = [
  {
    key: 'download',
    tag: '客户端',
    title: '剪映小助手下载',
    desc: '一键把 AI 生成的视频导入剪映，草稿、配音、字幕自动生成，支持 Windows 与 macOS。',
    cta: '立即下载',
    icon: Download,
    accent: 'download',
    to: '/download',
  },
  {
    key: 'user-info',
    tag: '开发者',
    title: '获取 API 秘钥',
    desc: '前往个人中心查看、复制或重置 API 秘钥，在扣子插件、工作流与剪映小助手中调用。',
    cta: '获取秘钥',
    icon: Key,
    accent: 'api',
    to: '/user-info',
  },
  {
    key: 'api-docs',
    tag: '帮助文档',
    title: 'API 网关帮助文档',
    desc: '一站式处理视频剪辑自动化，专门为视频创作而生的 API 网关。',
    cta: '查看文档',
    icon: BookOpenText,
    accent: 'docs',
    href: 'https://docs.jcaigc.cn',
  },
];

const HeroSection = () => {
  return (
    <section className="home-hero">
      <div className="home-hero__backdrop" aria-hidden>
        <span className="home-hero__grid" />
        <span className="home-hero__orb home-hero__orb--1" />
        <span className="home-hero__orb home-hero__orb--2" />
      </div>

      <div className="container home-hero__inner">
        <span className="home-hero__badge">
          <Sparkle size={16} weight="fill" aria-hidden />
          简创 AIGC · AI 视频生产力平台
        </span>

        <h1 className="home-hero__title">
          AI 创作・<span className="home-hero__title-accent">一键成片</span>
        </h1>
        <p className="home-hero__subtitle">
          全球首个视频创作 API 网关，把创意交给 AI，把成片交给你
        </p>

        <div className="home-entry-grid">
          {HERO_ENTRIES.map((entry) => {
            const EntryIcon = entry.icon;
            const content = (
              <>
                <div className="home-entry__top">
                  <span className="home-entry__icon">
                    <EntryIcon size={28} weight="fill" aria-hidden />
                  </span>
                  <span className="home-entry__tag">{entry.tag}</span>
                </div>
                <h2 className="home-entry__title">{entry.title}</h2>
                <p className="home-entry__desc">{entry.desc}</p>
                <span className="home-entry__cta">
                  {entry.cta}
                  {entry.href ? (
                    <ArrowSquareOut size={18} weight="bold" aria-hidden />
                  ) : (
                    <ArrowRight size={18} weight="bold" aria-hidden />
                  )}
                </span>
              </>
            );
            const className = `home-entry home-entry--${entry.accent}`;

            if (entry.to) {
              return (
                <Link className={className} key={entry.key} to={entry.to}>
                  {content}
                </Link>
              );
            }

            return (
              <a
                className={className}
                href={entry.href}
                key={entry.key}
                target="_blank"
                rel="noopener noreferrer"
              >
                {content}
              </a>
            );
          })}
        </div>

        <div className="home-hero__meta">
          {HERO_META.map((item) => (
            <span className="home-hero__meta-item" key={item}>
              <CheckCircle size={16} weight="fill" aria-hidden />
              {item}
            </span>
          ))}
        </div>

        <nav className="home-hero__shortcuts" aria-label="页面内容快速导航">
          {SECTION_SHORTCUTS.map((item) => (
            <a className="home-hero__shortcut" href={item.href} key={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
};

export default HeroSection;
